import { useReactiveVar } from "@apollo/client";
import React, { useCallback, useMemo } from "react";

import HeaderView from "./HeaderView";
import {
  DEFAULT_MAX_AGE,
  DEFAULT_MAX_COST,
  DEFAULT_MAX_MONTHS_REMAINING,
  DEFAULT_MIN_AGE,
  DEFAULT_MIN_COST,
  DEFAULT_MIN_MONTHS_REMAINING,
  filterValuesVar,
} from "@/features/similar-players/stores/filterValues";

import {
  RosterManagementLeagueFragment,
  RosterManagementNationalityFragment,
  useRosterManagementListMyOrganizationsLeaguesQuery,
  useRosterManagementListNationalitiesQuery,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useTheme } from "@/providers/ThemeProvider";
import removeEmptyKeys from "@/utils/removeEmptyKeys";

/**
 * Container component props for similar players header
 */
interface Props {
  playerName?: string;
  valueAccordion: string[];
  onPressAccordion: () => void;
}

/**
 * Container component for similar players header with filtering capabilities.
 * @param root0 - Component props
 * @param root0.playerName - Name of the player to find similar players for
 * @param root0.valueAccordion - Current accordion expanded state values
 * @param root0.onPressAccordion - Callback when accordion trigger is pressed
 */
const HeaderContainer = ({
  playerName,
  valueAccordion,
  onPressAccordion,
}: Props) => {
  const filterValues = useReactiveVar(filterValuesVar) ?? {};
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { data: leagueData, loading: loadingLeagues } =
    useRosterManagementListMyOrganizationsLeaguesQuery();
  const { data: nationalityData, loading: loadingNationalities } =
    useRosterManagementListNationalitiesQuery({
      variables: { first: 1000 },
    });

  const nationalities = useMemo(
    () =>
      [...(nationalityData?.listNationalities?.edges ?? [])]
        .map(edge => edge.node)
        .sort((current, next) => current.name.localeCompare(next.name)),
    [nationalityData?.listNationalities?.edges]
  );

  const groupedLeagues =
    leagueData?.listMyOrganizationsLeagues.edges
      .filter(league => league.node.name !== "")
      .sort((current, next) => {
        const countryNameCompare = (current.node?.country ?? "").localeCompare(
          next.node?.country ?? ""
        );
        if (countryNameCompare !== 0) {
          return countryNameCompare;
        }
        return current.node.name.localeCompare(next.node.name);
      })
      .map(
        e =>
          ({
            id: e.node.id,
            name: `${e.node.country} - ${e.node.name}`,
          }) as RosterManagementLeagueFragment
      ) ?? [];
  const onSelectLeagues = useCallback(
    (selectedLeagues: RosterManagementLeagueFragment[]) => {
      filterValuesVar({
        ...filterValuesVar(),
        leagues: selectedLeagues.length === 0 ? null : selectedLeagues,
      });
    },
    []
  );

  const onSelectNationalities = useCallback(
    (selectedNationalities: RosterManagementNationalityFragment[]) => {
      filterValuesVar({
        ...filterValuesVar(),
        nationalities:
          selectedNationalities.length === 0 ? null : selectedNationalities,
      });
    },
    []
  );

  const onChangeAge = useCallback((value: number[]) => {
    filterValuesVar(
      removeEmptyKeys({
        ...filterValuesVar(),
        minAge: DEFAULT_MIN_AGE === value[0] ? null : value[0],
        maxAge: DEFAULT_MAX_AGE === value[1] ? null : value[1],
      })
    );
  }, []);

  const onChangeSalary = useCallback((value: number[]) => {
    filterValuesVar(
      removeEmptyKeys({
        ...filterValuesVar(),
        minSalary: DEFAULT_MIN_COST === value[0] ? null : value[0],
        maxSalary: DEFAULT_MAX_COST === value[1] ? null : value[1],
      })
    );
  }, []);

  const onChangeMonthsRemaining = useCallback((value: number[]) => {
    filterValuesVar(
      removeEmptyKeys({
        ...filterValuesVar(),
        minMonthsRemaining:
          DEFAULT_MIN_MONTHS_REMAINING === value[0] ? null : value[0],
        maxMonthsRemaining:
          DEFAULT_MAX_MONTHS_REMAINING === value[1] ? null : value[1],
      })
    );
  }, []);

  const handleReset = useCallback(() => filterValuesVar(null), []);

  // Note: Filter cleanup is handled by the parent MainContainer, not here
  // The parent component manages the filter lifecycle to avoid issues with
  // component remounting during slider interactions on web

  const ageValue = useMemo(
    () => [
      filterValues.minAge ?? DEFAULT_MIN_AGE,
      filterValues.maxAge ?? DEFAULT_MAX_AGE,
    ],
    [filterValues.minAge, filterValues.maxAge]
  );

  const salaryValue = useMemo(
    () => [
      filterValues.minSalary ?? DEFAULT_MIN_COST,
      filterValues.maxSalary ?? DEFAULT_MAX_COST,
    ],
    [filterValues.minSalary, filterValues.maxSalary]
  );

  const monthsRemainingValue = useMemo(
    () => [
      filterValues.minMonthsRemaining ?? DEFAULT_MIN_MONTHS_REMAINING,
      filterValues.maxMonthsRemaining ?? DEFAULT_MAX_MONTHS_REMAINING,
    ],
    [filterValues.minMonthsRemaining, filterValues.maxMonthsRemaining]
  );

  return (
    <HeaderView
      playerName={playerName}
      groupedLeagues={groupedLeagues}
      loadingLeagues={loadingLeagues}
      nationalities={nationalities}
      loadingNationalities={loadingNationalities}
      nationalitiesValue={filterValues?.nationalities ?? []}
      isSmallScreen={isSmallScreen}
      activeFilterCount={
        Object.keys(removeEmptyKeys({ ...filterValues })).length
      }
      leaguesValue={filterValues?.leagues ?? []}
      ageValue={ageValue}
      salaryValue={salaryValue}
      monthsRemainingValue={monthsRemainingValue}
      valueAccordion={valueAccordion}
      onSelectLeagues={onSelectLeagues}
      onSelectNationalities={onSelectNationalities}
      onChangeAge={onChangeAge}
      onChangeSalary={onChangeSalary}
      onChangeMonthsRemaining={onChangeMonthsRemaining}
      onReset={handleReset}
      onPressAccordion={onPressAccordion}
      isDark={isDark}
    />
  );
};

export default HeaderContainer;
